// src/core/frame_cache.ts
// FrameCache — append-only timeline storage for UIFrame snapshots.
//
// Design decisions:
// - Primary key: seq (monotonic, from backend)
// - Frame identity: seq + frame_checksum
// - Bounded window: maxFrames + time-window (relative to latest, not wall clock)
// - Snapshot: every 500 frames for O(1) replay seek

import { EventEmitter } from 'eventemitter3';
import type { UIFrame } from './ui_contract';

export interface Frame {
  frame_id: number;          // 1-based position in timeline (internal)
  seq: number;               // from backend (monotonic primary key)
  generated_at_ms: number;   // business time
  frame_checksum: string;    // dedup / change detection
  data: UIFrame;             // full snapshot
  isDuplicate: boolean;       // true if checksum unchanged from previous frame
}

export interface FrameCacheConfig {
  maxFrames?: number;        // default: 100,000
  maxAgeMs?: number;         // default: 30 min (relative to latest)
  snapshotInterval?: number; // default: 500
}

const DEFAULT_CONFIG = {
  maxFrames: 100_000,
  maxAgeMs: 30 * 60 * 1000,
  snapshotInterval: 500,
};

export class FrameCache {
  // Storage: append-only timeline
  private timeline: Frame[] = [];

  // Indexes
  private bySeq = new Map<number, Frame>();      // seq → Frame
  private byTime = new Map<number, Frame>();     // generated_at_ms → Frame

  // Snapshots for replay seek (every N frames)
  private snapshots = new Map<number, Frame>();  // frame_id → Frame

  private config: Required<FrameCacheConfig>;
  private emitter = new EventEmitter();

  constructor(config: FrameCacheConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config } as Required<FrameCacheConfig>;
  }

  // ---- Insert ----

  /**
   * Push a new frame into the cache.
   * Called by wsClient when a UIFrame WS message arrives.
   */
  push(seq: number, generated_at_ms: number, frame_checksum: string, data: UIFrame): Frame {
    // Evict BEFORE insert (keep bounded)
    this.evictIfNeeded();

    const frame_id = this.timeline.length + 1;
    const prev = this.timeline[this.timeline.length - 1];
    const isDuplicate = prev?.frame_checksum === frame_checksum;

    const frame: Frame = {
      frame_id,
      seq,
      generated_at_ms,
      frame_checksum,
      data,
      isDuplicate,
    };

    // Append to timeline (O(1))
    this.timeline.push(frame);
    this.bySeq.set(seq, frame);
    this.byTime.set(generated_at_ms, frame);

    // Create snapshot every N frames
    if (frame_id % this.config.snapshotInterval === 0) {
      this.snapshots.set(frame_id, frame);
    }

    this.emitter.emit('frame:added', frame);
    return frame;
  }

  // ---- Query ----

  /** O(1) latest frame */
  getLatest(): Frame | undefined {
    return this.timeline[this.timeline.length - 1];
  }

  /** O(1) by seq */
  getBySeq(seq: number): Frame | undefined {
    return this.bySeq.get(seq);
  }

  /** O(1) by frame_id (1-based position) */
  getById(frameId: number): Frame | undefined {
    if (frameId < 1 || frameId > this.timeline.length) return undefined;
    return this.timeline[frameId - 1];
  }

  /** O(log n) by timestamp — returns nearest frame at or after ts */
  getByTime(ts: number): Frame | undefined {
    if (this.timeline.length === 0) return undefined;
    let lo = 0, hi = this.timeline.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (this.timeline[mid].generated_at_ms < ts) lo = mid + 1;
      else hi = mid;
    }
    return this.timeline[lo] ?? this.timeline[lo - 1];
  }

  /** O(1) range by frame_id */
  getRange(startFrameId: number, endFrameId: number): Frame[] {
    if (startFrameId > endFrameId) return [];
    const start = Math.max(1, startFrameId) - 1;
    const end = Math.min(this.timeline.length, endFrameId);
    return this.timeline.slice(start, end);
  }

  /** O(1) range by seq */
  getSeqRange(startSeq: number, endSeq: number): Frame[] {
    const frames: Frame[] = [];
    for (const frame of this.timeline) {
      if (frame.seq >= startSeq && frame.seq <= endSeq) {
        frames.push(frame);
      }
    }
    return frames;
  }

  // ---- Snapshot (Replay Seek) ----

  /**
   * Seek to target frame using snapshot + delta replay.
   * Returns the Frame at targetId, reconstructed from nearest prior snapshot.
   */
  seekToFrameId(targetId: number): Frame | undefined {
    if (targetId < 1 || targetId > this.timeline.length) return undefined;
    if (targetId === 1) return this.timeline[0];

    // Find nearest snapshot at or before targetId
    let snapshotId = 0;
    for (const [id, frame] of this.snapshots) {
      if (id <= targetId && id > snapshotId) snapshotId = id;
    }

    const snapshot = this.snapshots.get(snapshotId);
    if (!snapshot) {
      // No snapshot, return raw frame
      return this.timeline[targetId - 1];
    }

    // Apply deltas from snapshot+1 to target
    return this.applyDeltasToSnapshot(snapshot, snapshotId + 1, targetId);
  }

  /** Apply delta frames to snapshot to reconstruct target frame */
  private applyDeltasToSnapshot(snapshot: Frame, startId: number, endId: number): Frame {
    if (startId > endId) return snapshot;

    const deltas = this.getRange(startId, endId);
    let result = snapshot;

    for (const delta of deltas) {
      // Duplicate frames mean state unchanged — skip
      if (!delta.isDuplicate) {
        result = delta;
      }
    }
    return result;
  }

  // ---- Eviction ----

  private evictIfNeeded(): void {
    const latestTs = this.timeline[this.timeline.length - 1]?.generated_at_ms ?? Date.now();
    const cutoffTs = latestTs - this.config.maxAgeMs;

    // Count-based eviction
    while (this.timeline.length >= this.config.maxFrames) {
      this.evictFirst();
    }

    // Time-based eviction (relative to latest, not wall clock)
    while (this.timeline.length > 0 && this.timeline[0].generated_at_ms < cutoffTs) {
      this.evictFirst();
    }
  }

  private evictFirst(): void {
    const first = this.timeline.shift();
    if (!first) return;

    this.bySeq.delete(first.seq);
    this.byTime.delete(first.generated_at_ms);
    this.emitter.emit('frame:evicted', first);
  }

  // ---- Stats ----

  getStats() {
    return {
      totalFrames: this.timeline.length,
      latestSeq: this.timeline[this.timeline.length - 1]?.seq,
      oldestTs: this.timeline[0]?.generated_at_ms,
      latestTs: this.timeline[this.timeline.length - 1]?.generated_at_ms,
      snapshotCount: this.snapshots.size,
    };
  }

  // ---- Events ----

  on(event: string, handler: (...args: any[]) => void) { this.emitter.on(event, handler); }
  off(event: string, handler: (...args: any[]) => void) { this.emitter.off(event, handler); }

  // ---- Clear ----

  clear(): void {
    this.timeline = [];
    this.bySeq.clear();
    this.byTime.clear();
    this.snapshots.clear();
    this.emitter.removeAllListeners();
  }
}
